import React, { useState, useEffect, useMemo } from "react";

import { BaseTextFieldProps } from "@mui/material";
import Stack from "@mui/material/Stack";
import { isEqual } from "date-fns";
import { format } from "date-fns-tz";
import { Range, RangeKeyDict, DateRange } from "react-date-range";
import { useTranslation } from "react-i18next";
import { FiCalendar, FiX } from "react-icons/fi";

import Button, { Color } from "../../Button";
import Input from "../index";
import useStyles, { StyledMenu } from "./styles";
import { useAppState } from "Context/StateContext";

interface InputDateRangeProps extends BaseTextFieldProps {
  name?: string;
  label?: React.ReactNode;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  min?: string | number;
  max?: string | number;
  pStartDate?: Date | undefined;
  pEndDate?: Date | undefined;
  showClearButton?: boolean;
  onChange?: (range: Range) => void;
}

const InputDateRange = (props: InputDateRangeProps) => {
  const {
    name,
    label,
    error,
    errorMessage,
    disabled = false,
    min,
    max,
    pStartDate,
    pEndDate,
    showClearButton = false,
    onChange,
    ...rest
  } = props;

  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [rangeText, setRangeText] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open: boolean = !!anchorEl;

  const [range, setRange] = useState<Range[]>([
    {
      startDate: pStartDate,
      endDate: pEndDate,
      key: "selection",
    },
  ]);

  const userDatePreference = currentUser?.preferences?.dateFormat;

  const dateFormat = useMemo(() => {
    return userDatePreference === "MM_DD_YYYY" ? "MM/dd/yyyy" : "dd/MM/yyyy";
  }, [userDatePreference]);

  const datePlaceholder = useMemo(() => {
    return userDatePreference === "MM_DD_YYYY"
      ? "mm/dd/yyyy - mm/dd/yyyy"
      : "dd/mm/yyyy - dd/mm/yyyy";
  }, [userDatePreference]);

  useEffect(() => {
    if (!pStartDate && !pEndDate) {
      setRangeText("");
      setRange([
        {
          startDate: undefined,
          endDate: undefined,
          key: "selection",
        },
      ]);
    }
  }, [pStartDate, pEndDate]);

  const hasSelectedRange = useMemo(
    () => range && !!range.length && range[0].startDate && range[0].endDate,
    [range]
  );

  const checkBothDatesAreSelected = (selectedRange: Range): boolean => {
    return (
      !!selectedRange?.startDate &&
      !!selectedRange?.endDate &&
      !isEqual(selectedRange.startDate, selectedRange.endDate)
    );
  };

  useEffect(() => {
    const selectedRange = range[0];

    if (!selectedRange) {
      setRangeText("");
      return;
    }

    if (!checkBothDatesAreSelected(selectedRange)) {
      if (selectedRange?.startDate) {
        const formattedStartDate = format(selectedRange.startDate, dateFormat);
        const secondDatePlaceholder =
          userDatePreference === "MM_DD_YYYY" ? "mm/dd/yyyy" : "dd/mm/yyyy";

        setRangeText(`${formattedStartDate} - ${secondDatePlaceholder}`);
        return;
      }

      setRangeText("");
    } else {
      const formattedStartDate = format(selectedRange.startDate || new Date(), dateFormat);
      const formattedEndDate = format(selectedRange.endDate || new Date(), dateFormat);

      setRangeText(`${formattedStartDate} - ${formattedEndDate}`);
    }
  }, [range, dateFormat, userDatePreference]);

  useEffect(() => {
    if (onChange && checkBothDatesAreSelected(range[0])) {
      onChange(range[0]);
      setAnchorEl(null);
    }
  }, [range]);

  const handleSelectRange = (item: RangeKeyDict) => {
    setRange([item.selection]);
  };

  const handleClearDateRange = () => {
    setAnchorEl(null);
    setRangeText("");
    setRange([
      {
        startDate: undefined,
        endDate: undefined,
        key: "selection",
      },
    ]);

    if (onChange) {
      onChange({ startDate: undefined, endDate: undefined });
    }
  };

  const endAdornment = (
    <Stack className={classes.endAdornment} direction="row" spacing={1}>
      {hasSelectedRange && <FiX className={classes.clearDateIcon} onClick={handleClearDateRange} />}
      <FiCalendar />
    </Stack>
  );

  const handleOpenDatePicker = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <Input
        id="date-range-picker-input"
        name={name}
        label={label}
        value={rangeText}
        onClickCapture={handleOpenDatePicker}
        formControlClassName={classes.input}
        rightIcon={endAdornment}
        placeholder={datePlaceholder}
        error={error}
        errorMessage={errorMessage}
        disabled={disabled}
        {...rest}
      />

      <StyledMenu
        id="date-range-picker"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <DateRange
          showDateDisplay={false}
          preventSnapRefocus
          ranges={range}
          onChange={handleSelectRange}
          minDate={min ? new Date(min) : undefined}
          maxDate={max ? new Date(max) : undefined}
          dateDisplayFormat={dateFormat}
        />
      </StyledMenu>

      {showClearButton && rangeText && (
        <Button customColor={Color.SECONDARY} onClick={() => handleClearDateRange()}>
          {translate("clear")}
        </Button>
      )}
    </>
  );
};

export default InputDateRange;
